'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import Script from 'next/script'
import clsx from 'clsx'
import { getCookie } from 'cookies-next'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { GTMAppScript } from '@/services/gtm'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { Button } from 'ui/src/components/Button/Button'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import type { OneTrustApi } from '../GTMLoader'
import { useSaveConsent } from '../GTMLoader'
import { useUpdateShopSessionConsent } from '../useUpdateShopSessionConsent'
import { loadOneTrustAtom } from './OneTrustAtom'
import { actionButton, actionsContainer, buttonGroup, cookieBanner, linkButton } from './CookieConsent.css'

const COOKIE_CONSENT_SCRIPT_ID = {
  TEST: '628f5fee-1891-418c-9ed2-f893b8a3998a-test',
  PRODUCTION: '628f5fee-1891-418c-9ed2-f893b8a3998a',
}

// Only use prod script in production
const scriptId =
  process.env.VERCEL_ENV === 'production' ? COOKIE_CONSENT_SCRIPT_ID.PRODUCTION : COOKIE_CONSENT_SCRIPT_ID.TEST

const COOKIE_CONSENT_HEADING_ID = 'cookie-consent-heading'
const COOKIE_CONSENT_DESCRIPTION_ID = 'cookie-consent-description'

type CookieConsent = {
  showBanner: boolean
  initializeGtm: boolean
  action: 'allowAll' | 'rejectAll' | 'openSettings' | null
}

export function CookieConsent() {
  const saveConsent = useSaveConsent()
  const cookieValue = getCookie('OptanonAlertBoxClosed')
  const [loadOneTrust, setLoadOneTrust] = useAtom(loadOneTrustAtom)
  const { t } = useTranslation(['cookieConsent', 'common'])
  const locale = useRoutingLocale()
  const bannerRef = useRef<HTMLDivElement>(null)

  const { updateShopSessionConsent } = useUpdateShopSessionConsent()

  // 1. Make sure we don't show the cookie banner before we have read the OneTrust cookie
  // 2. Initialize GTM on initial load to ensure consent values are sent from the start
  const [cookieConsent, setCookieConsent] = useState<CookieConsent>({
    showBanner: false,
    initializeGtm: true,
    action: null,
  })

  useEffect(() => {
    const isAlertBoxClosed = cookieValue !== undefined
    setCookieConsent((prevState) => ({
      ...prevState,
      initializeGtm: isAlertBoxClosed,
      showBanner: !isAlertBoxClosed,
    }))
  }, [cookieValue])

  // Focus management: move focus to dialog container when banner appears
  useEffect(() => {
    if (cookieConsent.showBanner && bannerRef.current) {
      bannerRef.current.focus()
    }
  }, [cookieConsent.showBanner])

  const handleOneTrustLoaded = () => {
    ;(window as unknown as Window & { OptanonWrapper: () => void }).OptanonWrapper = function () {
      const OneTrust = (window as unknown as Window & { OneTrust: OneTrustApi }).OneTrust

      // Hide OneTrust cookie banner since we render our own banner
      const banner = document.getElementById('onetrust-banner-sdk')
      if (banner) {
        banner.style.display = 'none'
      }

      if (cookieConsent.action === 'allowAll' && !OneTrust.IsAlertBoxClosed()) {
        OneTrust.AllowAll()
        saveConsent()
        updateShopSessionConsent()
        setCookieConsent((prevState) => ({
          ...prevState,
          initializeGtm: true,
        }))
      }

      if (cookieConsent.action === 'rejectAll' && !OneTrust.IsAlertBoxClosed()) {
        OneTrust.RejectAll()
        saveConsent()
        updateShopSessionConsent()
        setCookieConsent((prevState) => ({
          ...prevState,
          initializeGtm: true,
        }))
      }

      if (cookieConsent.action === 'openSettings') {
        OneTrust.ToggleInfoDisplay()
        // Remove overflow hidden when settings dialog is closed
        OneTrust.OnConsentChanged(() => {
          const html = document.querySelector('html')
          const body = document.querySelector('body')
          if (html && body) {
            html.style.overflow = 'unset'
            body.style.overflow = 'unset'
          }
          updateShopSessionConsent()
          setCookieConsent((prevState) => ({
            ...prevState,
            initializeGtm: true,
          }))
        })
      }
    }
  }

  const handleAccept = () => {
    setLoadOneTrust(true)
    setCookieConsent((prevState) => ({
      ...prevState,
      action: 'allowAll',
      showBanner: false,
    }))
  }

  const handleReject = () => {
    setLoadOneTrust(true)
    setCookieConsent((prevState) => ({
      ...prevState,
      action: 'rejectAll',
      showBanner: false,
    }))
  }

  const handleCookieSettings = () => {
    setLoadOneTrust(true)
    setCookieConsent((prevState) => ({
      ...prevState,
      action: 'openSettings',
      showBanner: false,
    }))
  }

  return (
    <>
      {loadOneTrust && (
        <Script
          id="onetrust-loader"
          src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
          data-document-language="true"
          type="text/javascript"
          data-domain-script={scriptId}
          onLoad={handleOneTrustLoaded}
        />
      )}

      {cookieConsent.initializeGtm && <GTMAppScript />}

      {cookieConsent.showBanner && (
        <div
          ref={bannerRef}
          className={cookieBanner}
          data-theme="dark"
          role="dialog"
          aria-modal="false"
          aria-labelledby={COOKIE_CONSENT_HEADING_ID}
          aria-describedby={COOKIE_CONSENT_DESCRIPTION_ID}
          // tabIndex={-1} makes the container programmatically focusable (via ref.focus())
          // but not keyboard navigable. When focused, screen readers announce the dialog
          // with its aria-labelledby and aria-describedby content.
          tabIndex={-1}
        >
          <div className={yStack({ gap: 'xxs' })}>
            <Text id={COOKIE_CONSENT_HEADING_ID} color="textPrimary">
              {t('cookieConsent:COOKIE_CONSENT_HEADING')}
            </Text>
            <Text id={COOKIE_CONSENT_DESCRIPTION_ID} color="textSecondary" size={{ _: 'xs', sm: 'md' }}>
              {t('cookieConsent:COOKIE_CONSENT_BODY')}
              <Link
                href={PageLink.cookiesInfo({ locale }).pathname}
                className={clsx(linkButton, sprinkles({ marginLeft: 'xxs' }))}
                rel="noopener"
                target="_blank"
                aria-label={`${t('common:READ_MORE')} - opens in new tab`}
              >
                {t('common:READ_MORE')}
              </Link>
            </Text>
          </div>
          <div className={actionsContainer}>
            <button type="button" className={linkButton} onClick={handleCookieSettings}>
              {t('cookieConsent:COOKIE_CONSENT_SETTINGS_BUTTON')}
            </button>
            <div className={buttonGroup}>
              <Button className={actionButton} variant="primary" size="medium" onClick={handleReject}>
                {t('cookieConsent:COOKIE_CONSENT_DENY_ALL_BUTTON')}
              </Button>
              <Button className={actionButton} variant="primary" size="medium" onClick={handleAccept}>
                {t('cookieConsent:COOKIE_CONSENT_ALLOW_ALL_BUTTON')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
